import dayjs from "dayjs";
import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { usePaymentContext } from "@/contexts/PaymentContext";
import InstallmentBreakdownDrawer from "@/features/flights/components/flight-purchase/InstallmentBreakdownDrawer";
import { useLocaleInfo } from "@/hooks/useLocaleInfo";
import { formatCurrency } from "@/utils/currency";

interface InstallmentDetailsProps {
  promoCodeDiscount?: number;
  totalPrice: number;
}

const InstallmentDetails = ({
  promoCodeDiscount,
  totalPrice,
}: InstallmentDetailsProps) => {
  const intl = useIntl();
  const [showBreakdown, setShowBreakdown] = useState(false);
  const { locale } = useLocaleInfo();
  const { selectedInstallments } = usePaymentContext();
  const nextMonth = dayjs().add(1, "month").locale(locale).format("MMMM");

  return (
    <>
      <div className="section-card mt-2 flex flex-col gap-2 text-sm">
        <div className="flex justify-between">
          <span className="text-text-tertiary">
            {intl.formatMessage({
              defaultMessage: "Next payment",
              description:
                "Label for the date of the next scheduled instalment payment",
            })}
          </span>
          <span className="font-bold text-text-primary">{nextMonth}</span>
        </div>
        {promoCodeDiscount ? (
          <div className="flex justify-between">
            <div className="text-text-tertiary">
              {intl.formatMessage({
                defaultMessage: "Promo code discount",
                description:
                  "Line item label for the promo code discount in the price summary",
              })}
            </div>
            <div className="font-bold text-core-accent-500">
              {formatCurrency(promoCodeDiscount)}
            </div>
          </div>
        ) : null}
        <div className="flex justify-between">
          <span className="text-text-tertiary">
            {intl.formatMessage({
              defaultMessage: "Payment amount",
              description:
                "Label for the instalment payment amount in booking details",
            })}
          </span>
          <span className="font-bold text-text-primary">
            {intl.formatMessage(
              {
                defaultMessage: "{amount} per month",
                description: "Formatted monthly instalment amount",
              },
              {
                amount: formatCurrency(totalPrice / selectedInstallments),
              },
            )}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-text-tertiary">
            {intl.formatMessage({
              defaultMessage: "Total price",
              description: "Label for the total booking price",
            })}
          </span>
          <span className="font-bold text-text-primary">
            {formatCurrency(totalPrice)}
          </span>
        </div>
        <div className="mt-2">
          <Button
            color="primary"
            variant="outlined"
            size="small"
            onClick={() => setShowBreakdown(true)}
          >
            {intl.formatMessage({
              defaultMessage: "More details",
              description:
                "Link to show more details about the instalment payment schedule",
            })}
          </Button>
        </div>
      </div>
      <InstallmentBreakdownDrawer
        promoCodeDiscount={promoCodeDiscount}
        totalPrice={totalPrice}
        open={showBreakdown}
        onClose={() => setShowBreakdown(false)}
      />
    </>
  );
};

export default InstallmentDetails;
